import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

export interface FormFieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function FormField({ label, htmlFor, error, hint, required, children }: FormFieldProps) {
  const autoId = useId();
  // Auto-wire label↔control when no explicit htmlFor (WCAG 1.3.1 / 3.3.2).
  let controlId = htmlFor;
  let child = children;
  if (!controlId && isValidElement(children)) {
    const el = children as ReactElement<{ id?: string; 'aria-invalid'?: boolean }>;
    controlId = el.props.id ?? autoId;
    child = cloneElement(el, { id: controlId, ...(error ? { 'aria-invalid': true } : {}) });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        htmlFor={controlId}
        style={{
          fontSize: 'var(--t-sm)',
          fontWeight: 500,
          color: 'var(--text-2)',
        }}
      >
        {label}
        {required && (
          <span aria-hidden="true" style={{ color: 'var(--danger)', marginInlineStart: 2 }}>
            *
          </span>
        )}
      </label>
      {child}
      {error && (
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)' }} role="alert">
          {error}
        </span>
      )}
      {hint && !error && (
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{hint}</span>
      )}
    </div>
  );
}
